<template>
  <!-- Bank Reconciliation Upload Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-upload me-2"></i>
            Upload Bank Statement for Reconciliation
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Step Indicator -->
          <div class="step-indicator mb-4">
            <div class="step" :class="{active: currentStep >= 1, completed: currentStep > 1}">
              <div class="step-icon">1</div>
              <div class="step-title">Upload File</div>
            </div>
            <div class="step-line" :class="{active: currentStep > 1}"></div>
            <div class="step" :class="{active: currentStep >= 2, completed: currentStep > 2}">
              <div class="step-icon">2</div>
              <div class="step-title">Map Columns</div>
            </div>
            <div class="step-line" :class="{active: currentStep > 2}"></div>
            <div class="step" :class="{active: currentStep >= 3}">
              <div class="step-icon">3</div>
              <div class="step-title">Review & Confirm</div>
            </div>
          </div>

          <!-- Step 1: Upload File -->
          <div v-if="currentStep === 1">
            <div class="mb-3">
              <label class="form-label">Document Name <span class="text-danger">*</span></label>
              <input v-model="reconciliationData.documentName" type="text" class="form-control"
                     placeholder="e.g., CBE Statement January 2025">
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Bank Source <span class="text-danger">*</span></label>
                  <select v-model="reconciliationData.bankSource" class="form-select">
                    <option value="">Select Bank</option>
                    <option value="CBE">Commercial Bank of Ethiopia</option>
                    <option value="Awash">Awash Bank</option>
                    <option value="Dashen">Dashen Bank</option>
                    <option value="Abyssinia">Bank of Abyssinia</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Statement Period <span class="text-danger">*</span></label>
                  <input v-model="reconciliationData.period" type="month" class="form-control">
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Bank Account Number</label>
              <input v-model="reconciliationData.bankAccount" type="text" class="form-control"
                     placeholder="Enter account number (optional)">
            </div>

            <!-- File Upload -->
            <div class="card">
              <div class="card-body">
                <div class="text-center" v-if="!fileUploaded">
                  <i class="bx bx-cloud-upload text-primary" style="font-size: 48px;"></i>
                  <h6 class="mt-3">Upload Bank Statement</h6>
                  <p class="text-muted">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                  <input type="file" ref="fileInput" @change="handleFileUpload"
                         accept=".csv,.xlsx,.xls" class="d-none">
                  <button @click="$refs.fileInput.click()" class="btn btn-primary">
                    <i class="bx bx-upload me-1"></i> Choose File
                  </button>
                </div>
                <div v-else>
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 class="mb-1"><i class="bx bx-file me-2"></i>{{ uploadedFile.name }}</h6>
                      <small class="text-muted">{{ formatFileSize(uploadedFile.size) }}</small>
                    </div>
                    <button @click="removeFile" class="btn btn-sm btn-danger">
                      <i class="bx bx-x"></i> Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Map Columns -->
          <div v-else-if="currentStep === 2">
            <div class="alert alert-info">
              <i class="bx bx-info-circle me-2"></i>
              Map the columns from your bank statement to the required fields.
            </div>

            <div class="table-responsive">
              <table class="table table-bordered">
                <thead class="table-light">
                <tr>
                  <th>Required Field</th>
                  <th>Map to Column</th>
                  <th>Sample Data</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>Transaction Date</td>
                  <td>
                    <select v-model="columnMapping.date" class="form-select form-select-sm">
                      <option value="">Select Column</option>
                      <option v-for="col in availableColumns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </td>
                  <td>
                    <small class="text-muted">{{ getSampleData('date') }}</small>
                  </td>
                </tr>
                <tr>
                  <td>Reference Number</td>
                  <td>
                    <select v-model="columnMapping.reference" class="form-select form-select-sm">
                      <option value="">Select Column</option>
                      <option v-for="col in availableColumns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </td>
                  <td>
                    <small class="text-muted">{{ getSampleData('reference') }}</small>
                  </td>
                </tr>
                <tr>
                  <td>Amount</td>
                  <td>
                    <select v-model="columnMapping.amount" class="form-select form-select-sm">
                      <option value="">Select Column</option>
                      <option v-for="col in availableColumns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </td>
                  <td>
                    <small class="text-muted">{{ getSampleData('amount') }}</small>
                  </td>
                </tr>
                <tr>
                  <td>Description (Optional)</td>
                  <td>
                    <select v-model="columnMapping.description" class="form-select form-select-sm">
                      <option value="">Select Column</option>
                      <option v-for="col in availableColumns" :key="col" :value="col">{{ col }}</option>
                    </select>
                  </td>
                  <td>
                    <small class="text-muted">{{ getSampleData('description') }}</small>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Preview -->
            <div class="card mt-3">
              <div class="card-header">
                <h6 class="mb-0">Data Preview (First 5 rows)</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Reference</th>
                      <th>Amount</th>
                      <th>Description</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(row, index) in previewData" :key="index">
                      <td>{{ row.date }}</td>
                      <td>{{ row.reference }}</td>
                      <td>{{ formatAmount(row.amount) }}</td>
                      <td>{{ row.description || '-' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: Review & Confirm -->
          <div v-else-if="currentStep === 3">
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">Reconciliation Summary</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <p><strong>Document Name:</strong> {{ reconciliationData.documentName }}</p>
                    <p><strong>Bank Source:</strong> {{ reconciliationData.bankSource }}</p>
                    <p><strong>Period:</strong> {{ formatPeriod(reconciliationData.period) }}</p>
                  </div>
                  <div class="col-md-6">
                    <p><strong>Total Transactions:</strong> {{ parsedTransactions.length }}</p>
                    <p><strong>Total Amount:</strong> ETB {{ formatAmount(totalTransactionAmount) }}</p>
                    <p><strong>Date Range:</strong> {{ transactionDateRange }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Transaction Summary -->
            <div class="card mt-3">
              <div class="card-header">
                <h6 class="mb-0">Transaction Summary</h6>
              </div>
              <div class="card-body">
                <div class="alert alert-warning">
                  <i class="bx bx-info-circle me-2"></i>
                  The system will automatically match transactions with settlement batches based on reference numbers and amounts.
                  Unmatched transactions can be manually reconciled later.
                </div>
                <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                  <table class="table table-sm mb-0">
                    <thead class="table-light sticky-top">
                    <tr>
                      <th>Date</th>
                      <th>Reference</th>
                      <th class="text-end">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(trans, index) in parsedTransactions" :key="index">
                      <td>{{ trans.date }}</td>
                      <td>{{ trans.reference }}</td>
                      <td class="text-end">ETB {{ formatAmount(trans.amount) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <button v-if="currentStep > 1" @click="previousStep" class="btn btn-secondary me-auto">
            <i class="bx bx-chevron-left me-1"></i> Previous
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button v-if="currentStep < 3" @click="nextStep" class="btn btn-primary" :disabled="!canProceedToNext">
            Next <i class="bx bx-chevron-right ms-1"></i>
          </button>
          <button v-else @click="uploadReconciliation" class="btn btn-success" :disabled="uploading">
            <span v-if="uploading" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-check-circle me-1"></i>
            Upload & Reconcile
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export default {
  name: "ReconciliationUploadModal",
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 1,
      uploading: false,
      reconciliationData: {
        documentName: '',
        period: '',
        bankSource: '',
        bankAccount: ''
      },
      uploadedFile: null,
      fileUploaded: false,
      fileContent: null,
      availableColumns: [],
      columnMapping: {
        date: '',
        reference: '',
        amount: '',
        description: ''
      },
      parsedTransactions: [],
      previewData: []
    };
  },

  computed: {
    canProceedToNext() {
      if (this.currentStep === 1) {
        return this.reconciliationData.documentName &&
            this.reconciliationData.period &&
            this.reconciliationData.bankSource &&
            this.fileUploaded;
      } else if (this.currentStep === 2) {
        return this.columnMapping.date &&
            this.columnMapping.reference &&
            this.columnMapping.amount;
      }
      return false;
    },

    totalTransactionAmount() {
      return this.parsedTransactions.reduce((sum, trans) => sum + parseFloat(trans.amount || 0), 0);
    },

    transactionDateRange() {
      if (this.parsedTransactions.length === 0) return '-';
      const dates = this.parsedTransactions.map(t => new Date(t.date));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      return `${this.formatDate(minDate)} - ${this.formatDate(maxDate)}`;
    }
  },

  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFile = file;
        this.fileUploaded = true;
        this.parseFile(file);
      }
    },

    async parseFile(file) {
      const reader = new FileReader();

      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        // Parse CSV file
        reader.onload = async (e) => {
          const csvText = e.target.result;
          await this.parseCSV(csvText);
        };
        reader.readAsText(file);
      } else if (file.type.includes('spreadsheet') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Parse Excel file
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          await this.parseExcel(data);
        };
        reader.readAsArrayBuffer(file);
      }
    },

    async parseCSV(csvText) {
      try {
        // Use Papa Parse to parse CSV
        const results = Papa.parse(csvText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          delimitersToGuess: [',', '\t', '|', ';']
        });

        if (results.errors.length > 0) {
          console.error('CSV parsing errors:', results.errors);
          alert('Error parsing CSV file. Please check the file format.');
          return;
        }

        this.fileContent = results.data;
        this.availableColumns = results.meta.fields || [];

        // Auto-detect common column mappings
        this.autoDetectColumns();
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('Failed to parse CSV file');
      }
    },

    async parseExcel(data) {
      try {
        // Use SheetJS to parse Excel
        const workbook = XLSX.read(data, {
          type: 'array',
          cellDates: true,
          cellNF: false,
          cellText: false
        });

        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          raw: false,
          dateNF: 'yyyy-mm-dd'
        });

        if (jsonData.length < 2) {
          alert('Excel file appears to be empty');
          return;
        }

        // Extract headers and data
        const headers = jsonData[0];
        const rows = jsonData.slice(1);

        // Convert to array of objects
        this.fileContent = rows.map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            obj[header] = row[index];
          });
          return obj;
        }).filter(row => Object.values(row).some(val => val !== null && val !== ''));

        this.availableColumns = headers;

        // Auto-detect common column mappings
        this.autoDetectColumns();
      } catch (error) {
        console.error('Error parsing Excel:', error);
        alert('Failed to parse Excel file');
      }
    },

    autoDetectColumns() {
      // Auto-detect date column
      const dateColumns = this.availableColumns.filter(col =>
          col.toLowerCase().includes('date') ||
          col.toLowerCase().includes('transaction') ||
          col.toLowerCase().includes('posted')
      );
      if (dateColumns.length > 0) {
        this.columnMapping.date = dateColumns[0];
      }

      // Auto-detect reference column
      const refColumns = this.availableColumns.filter(col =>
          col.toLowerCase().includes('reference') ||
          col.toLowerCase().includes('ref') ||
          col.toLowerCase().includes('number') ||
          col.toLowerCase().includes('transaction id')
      );
      if (refColumns.length > 0) {
        this.columnMapping.reference = refColumns[0];
      }

      // Auto-detect amount column
      const amountColumns = this.availableColumns.filter(col =>
          col.toLowerCase().includes('amount') ||
          col.toLowerCase().includes('credit') ||
          col.toLowerCase().includes('deposit') ||
          col.toLowerCase().includes('value')
      );
      if (amountColumns.length > 0) {
        this.columnMapping.amount = amountColumns[0];
      }

      // Auto-detect description column
      const descColumns = this.availableColumns.filter(col =>
          col.toLowerCase().includes('description') ||
          col.toLowerCase().includes('desc') ||
          col.toLowerCase().includes('details') ||
          col.toLowerCase().includes('narration') ||
          col.toLowerCase().includes('particular')
      );
      if (descColumns.length > 0) {
        this.columnMapping.description = descColumns[0];
      }
    },

    removeFile() {
      this.uploadedFile = null;
      this.fileUploaded = false;
      this.fileContent = null;
      this.availableColumns = [];
      this.$refs.fileInput.value = '';
    },

    nextStep() {
      if (this.canProceedToNext) {
        if (this.currentStep === 2) {
          this.processTransactions();
        }
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    processTransactions() {
      // Map the file content to our transaction format
      this.parsedTransactions = this.fileContent.map(row => {
        // Handle date parsing
        let dateValue = row[this.columnMapping.date];
        if (dateValue) {
          // If it's already a Date object from Excel parsing
          if (dateValue instanceof Date) {
            dateValue = dateValue.toISOString().split('T')[0];
          } else if (typeof dateValue === 'string') {
            // Try to parse various date formats
            const parsedDate = new Date(dateValue);
            if (!isNaN(parsedDate)) {
              dateValue = parsedDate.toISOString().split('T')[0];
            }
          }
        }

        // Handle amount parsing
        let amountValue = row[this.columnMapping.amount];
        if (typeof amountValue === 'string') {
          // Remove currency symbols and commas
          amountValue = amountValue.replace(/[^0-9.-]/g, '');
        }

        return {
          date: dateValue || '',
          reference: String(row[this.columnMapping.reference] || ''),
          amount: parseFloat(amountValue) || 0,
          description: String(row[this.columnMapping.description] || '')
        };
      }).filter(trans => trans.amount > 0); // Only keep transactions with positive amounts

      // Set preview data (first 5 rows)
      this.previewData = this.parsedTransactions.slice(0, 5);
    },

    getSampleData(field) {
      if (!this.columnMapping[field] || !this.fileContent.length) return '-';
      const value = this.fileContent[0][this.columnMapping[field]];

      if (field === 'amount' && typeof value === 'number') {
        return this.formatAmount(value);
      }

      return value || '-';
    },

    async uploadReconciliation() {
      this.uploading = true;

      const data = {
        "function": "uploadBankReconciliation",
        ...this.reconciliationData,
        transactions: this.parsedTransactions
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('uploaded', res.data.data);
        } else {
          alert(res.data.message || 'Failed to upload reconciliation');
        }
      } catch (e) {
        console.error("Failed to upload reconciliation:", e);
        alert('Failed to upload reconciliation. Please try again.');
      } finally {
        this.uploading = false;
      }
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatPeriod(period) {
      if (!period) return '-';
      const [year, month] = period.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.step-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 8px;
}

.step.active .step-icon {
  background-color: #0dcaf0;
  color: white;
}

.step.completed .step-icon {
  background-color: #198754;
  color: white;
}

.step-title {
  font-size: 14px;
  color: #6c757d;
}

.step.active .step-title {
  color: #212529;
  font-weight: 500;
}

.step-line {
  width: 100px;
  height: 2px;
  background-color: #e9ecef;
  margin: 0 20px;
  margin-bottom: 28px;
}

.step-line.active {
  background-color: #198754;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>